import type { WriteOptions } from '@/utils/write-skill.util';
import type { BranchFilterGroup } from '@/types/filters'; // via the shim — never the component file directly
import type {
  CrmObjectType,
  CrmTriggerConfig,
} from '@/types/crm-enrichment-playbook';
import type { FieldMappingItem } from '@/store/features/crm.feature';
import type { CrmLookupConfig, CrmLookupIdentifier } from '@/types';
import type { SearchFilters } from '@/types/search-filters';
import type { CompanySearchFilters } from '@/utils/company-search.utils';
import type {
  AgentCompanyFilters,
  AgentPeopleFilters,
} from '@/containers/playbooks/custom-builder/search-audience-fields';
import type { RecordSkillOutputSchema } from '@/types/record-skill';

/**
 * Shared node contract for the Custom Playbook Builder (architecture §2.2).
 *
 * This module is the SINGLE SOURCE OF TRUTH for the wire shape. The backend
 * stores `graph` JSONB verbatim (camelCase, no FE↔BE transform layer) and
 * validates it structurally in `cleanse_api/utils/custom_playbook_graph.py`.
 */

// The FINAL 8 (build-v2-plan §4): ai_column / icp_score / delay / filter /
// export_csv are CUT; `find_committee` is renamed `find_people` (§4.2),
// matching the BE validator's node-type vocabulary.
// W5 adds the two CRM cleanup steps, which only follow a `crm_duplicates`
// trigger on an agent's play (design 2026-09-24, sections 5.3 and 5.4).
export type CustomNodeType =
  | 'trigger'
  | 'enrich'
  | 'find_people'
  | 'get_company'
  | 'find_open_jobs'
  | 'find_reviews'
  | 'research'
  | 'validate'
  | 'add_to_sequence'
  | 'export'
  | 'crm_action'
  | 'branch'
  | 'approval'
  | 'notify'
  | 'webhook'
  | 'wait'
  | 'code'
  | 'run_play'
  | 'find_meetings'
  /** A person's LinkedIn job history from two vendors (agent plays only). */
  | 'job_history'
  | 'review_duplicates'
  | 'merge_duplicates'
  /** A person's LinkedIn profile and the candidates weighed (agent plays only). */
  | 'identify_person'
  /** The account against a person's job history (agent plays only; free). */
  | 'match_company'
  /** Get Fireflies call notes and Get Fathom call notes: one step per tool. */
  | 'fireflies_notes'
  | 'fathom_notes'
  | 'gong_notes'
  // Notion's own steps (cleanse-api `utils/play_notion.py`), agent plays only.
  | 'notion_add_page'
  | 'notion_update_page'
  /** Google Docs' own step: one doc a record (an agent's play only). */
  | 'create_google_doc'
  /** Send an email: one email a person from the member's own Gmail or
   *  Outlook mailbox (an agent's play only). */
  | 'send_mailbox_email'
  /** Send on LinkedIn: one message or invitation a person from the member's
   *  own LinkedIn account (an agent's play only). */
  | 'send_linkedin_action'
  /** Count people: how many people at each record's company match an
   *  audience (an agent's play only; free). */
  | 'count_people'
  /** The Instantly capacity gate: lets on only as many people as the
   *  Instantly inboxes have room for (an agent's play only; free). */
  | 'instantly_capacity'
  /** A scheduled brief's one Clu turn (W2-24, an agent's play only). */
  | 'agent_task';

/**
 * What a record in a run is. A `duplicate_group` is one group of CRM records
 * a cleanup scan found; it may enter only Review duplicates, Merge duplicates
 * and Notify.
 */
export type RecordKind = 'company' | 'person' | 'duplicate_group';

export interface CustomGraph {
  version: 1;
  nodes: CustomNode[];
  edges: CustomEdge[];
  /** The cards a play Clu built from a spec shows, in order (cleanse-api
   *  `utils/play_blocks.py`, `describe_blocks`). Each holds the ids of its
   *  steps; the builder keeps them on every save. */
  blocks?: PlayBlock[];
}

/** One card of a built play: a hardened block, such as "Safe CRM upsert",
 *  and the steps it expands into. */
export interface PlayBlock {
  id: string;
  kind: string;
  label: string;
  summary: string;
  nodeIds: string[];
  inputs?: { kind: RecordKind | null };
  outputs?: { kind: RecordKind | null; fields: string[] };
}

export interface CustomNode {
  id: string;
  type: CustomNodeType;
  config: CustomNodeConfig;
  position: { x: number; y: number };
  /** Agent-play retry policy. Absence is the server's default retries for a
   *  step that supports them (3, but never a send that may have landed); an
   *  explicit `retries` is the person's own choice, 0 included. `then` (G29): stop (default), continue, or route down the step's error
   *  connection (`branchKey: "error"`). */
  onError?: { retries?: number; then?: 'stop' | 'continue' | 'route' };
}

export type HoldChoice = 'keep' | 'drop';

// Branch keys follow configured arm order; Approval keys are approved/rejected.
export interface CustomEdge {
  id: string;
  source: string;
  target: string;
  branchKey?: string;
}

/** The trigger object types the advanced builder offers. */
export type CustomCrmObjectType = CrmObjectType | 'Deal';

/**
 * The CRMs a play step can name. Every label or branch on it lists all
 * three with no default arm, so a CRM added later (Pipedrive) never
 * inherits another CRM's words. Job change stays the pair.
 */
export type PlayCrm = 'salesforce' | 'hubspot' | 'attio';

/** The play CRMs, in the order a picker offers them. */
export const PLAY_CRMS: readonly PlayCrm[] = ['salesforce', 'hubspot', 'attio'];

/** Each play CRM's name. */
export const PLAY_CRM_NAMES: Readonly<Record<PlayCrm, string>> = {
  salesforce: 'Salesforce',
  hubspot: 'HubSpot',
  attio: 'Attio',
};

export function isPlayCrm(type: unknown): type is PlayCrm {
  return (
    typeof type === 'string' && (PLAY_CRMS as readonly string[]).includes(type)
  );
}

/**
 * CRM trigger source (build-v2-plan §5.0). Intersection over the shared
 * `CrmTriggerConfig` — the preset types file is NOT edited; the
 * list-membership extension lives here, and `CrmTriggerBuilder` only ever
 * sees property-mode `conditions`.
 */
export type CustomCrmTriggerConfig = Omit<
  CrmTriggerConfig,
  'crmType' | 'objectType' | 'conditions'
> & {
  /**
   * The advanced builder supports both CRMs and all trigger object types.
   * The preset `CrmTriggerConfig` is deliberately narrowed to SF/Account and
   * is NOT edited — we widen `crmType`/`objectType` here via `Omit` so the
   * preset stays discriminated while the advanced builder gets the full set.
   */
  crmType: PlayCrm;
  /**
   * Widened past the preset's Contact/Account/Lead so a play can watch deals
   * and opportunities too. `CrmDeal` maps to a HubSpot deal and a Salesforce
   * opportunity; the label the UI shows follows `crmType`.
   */
  objectType: CustomCrmObjectType;
  conditions?: CrmTriggerConfig['conditions'];
  conditionType:
    | 'property'
    | 'list_membership'
    | 'stage_change'
    | 'stalled'
    | 'sixsense_stage';
  /** Native Deal source criteria. Stages are CRM values, never display labels.
   *  A 6sense stage trigger's are 6sense buying stages (`SIXSENSE_STAGES`). */
  stages?: string[];
  /** 6sense stage trigger: the lowest 6sense score (0 to 100), null for any. */
  minIntent?: number | null;
  /** 6sense stage trigger: one 6sense product's scores, absent for any. */
  product?: string;
  pipelineId?: string | null;
  amountMin?: number | null;
  stalledDays?: number;
  /** Read-only projections, including Contact parent Account/company fields. */
  fields?: string[];
  /**
   * Which records a change trigger picks up the first time it runs.
   * 'now' (default) only fires on changes from here on; 'historic' sweeps
   * everything that already matches before switching to change detection.
   */
  backfill?: 'now' | 'historic';
  /** list_membership mode (HubSpot list / Salesforce campaign/list view/report) */
  listId?: string;
  listName?: string;
  /**
   * Which Salesforce container `listId` names (build-v3 §2f). SF only —
   * HubSpot is implicitly a marketing list. Missing ⇒ `'campaign'`
   * (today's behavior; back-compat for saved graphs).
   */
  listKind?: 'campaign' | 'list_view' | 'report';
  /**
   * W1-32, an agent's play: `new_matches` (the default) or `all_matches`,
   * every matching contact or lead once each schedule period.
   */
  scope?: 'new_matches' | 'all_matches';
  /** All matches only: the most records a period takes, 1 to 250,000. */
  perPeriod?: number;
};

/**
 * "Skip leads already in CRM" toggle (build-v3 §2c layer 1). Canonical paths
 * (CR-5, SSOT for FE and BE): `trigger.config.cleanlist.skipInCrm` and
 * `find_people.config.skipInCrm` — NEVER a trigger-config-root sibling.
 */
export interface SkipInCrmConfig {
  enabled: boolean;
  crmType: PlayCrm;
  onlyIfOwned: boolean;
  /** "active in the last N days" sub-toggle; absent = no activity window */
  activeWithinDays?: number;
}

/** A job audience as saved by Clu and the new_jobs trigger. */
export interface JobFilters {
  job_titles?: string[];
  title_match?: 'phrase' | 'words';
  description_phrases?: string[];
  technologies?: string[];
  industries?: string[];
  company_sizes?: string[];
  countries?: string[];
  posted_within_days?: number;
  exclude_reposts?: boolean;
}

export interface JobAudienceSize {
  postings: number;
  firms: number;
  firmsIsLowerBound: boolean;
  inWords: string;
  sample: {
    company?: string | null;
    domain?: string | null;
    title?: string | null;
    location?: string | null;
    postedOn?: string | null;
  }[];
  jobFilters: JobFilters;
}

export interface PostFilters {
  author_urls?: string[];
  text_phrases?: string[];
  posted_within_days?: number;
}

export interface PostAudienceSize {
  postings: number;
  inWords: string;
  coverageNote: string;
  sample: {
    author?: string | null;
    post_url?: string | null;
    posted_on?: string | null;
    indexed_engagers: number;
  }[];
  postFilters: PostFilters;
  peopleFilters: SearchFilters;
}

export interface ToolFilters {
  technology: string;
  include_existing?: boolean;
}

export interface ToolAudienceSize {
  companies: number | null;
  canCreate: boolean;
  inWords: string;
  coverageNote: string;
  sample: { name?: string; domain?: string }[];
  toolFilters: ToolFilters;
  companyFilters: CompanySearchFilters;
  backfillCreditsPerCompany: number;
  newSignalCredits: number;
}

/** Cleanlist trigger source (people, companies or job postings). */
export interface CleanlistTriggerConfig {
  kind: 'people' | 'company' | 'jobs' | 'posts';
  /** Job audience in the engine's stored vocabulary (Marble M2). */
  jobFilters?: JobFilters;
  postFilters?: PostFilters;
  /** people search (same shape as Find People audience) */
  peopleFilters?: AgentPeopleFilters;
  companyFilters?: AgentCompanyFilters;
  /** Fire each company once for each distinct returned funding date. */
  refireOnNewSignal?: boolean;
  /**
   * Crustdata wire payloads derived FE-side at save by
   * `withDerivedTriggerWire` (graph-ops) from the raw blobs above — the
   * `audience_wire_filters` precedent (build-v3 §2b S1). The BE poll consumes
   * ONLY these; raw blobs are kept for re-editing. Absent on older drafts →
   * BE skips loudly with `missing_wire_filters`.
   */
  peopleWireFilters?: Record<string, unknown>;
  companyWireFilters?: Record<string, unknown>;
  /** fresh leads per scheduled run ("Find up to X new leads per run"); default 100, cap 200 */
  maxPerCycle?: number;
  /**
   * An agent's play: the leads it finds in one schedule period ("Find up to
   * 500 new leads per week"), 1 to 5,000, delivered in batches across ticks.
   * A company search followed by Find People counts the people Find People
   * delivers ("50 new people per week"), not companies. Used in place of
   * `maxPerCycle`; needs a daily, weekly or every N days schedule.
   */
  perPeriod?: number;
  /** max leads per company per run (S4; people kind only); 1–50, absent = no cap */
  maxPerCompany?: number;
  /** first-class already-in-CRM exclusion (§2c layer 1; canonical path per CR-5) */
  skipInCrm?: SkipInCrmConfig;
}

/**
 * Per-playbook run cadence (build-v3 §2a). Missing/absent ⇒ continuous
 * (every 30-min tick) ⇒ identical to today for every saved graph.
 * Resolution is ±30 min (the one fixed Temporal cron stays the tick).
 */
export interface TriggerSchedule {
  frequency:
    | 'continuous'
    | 'daily'
    /** Daily, Monday to Friday in its zone. */
    | 'weekdays'
    | 'every_n_days'
    | 'weekly'
    | 'monthly'
    | 'quarterly'
    /** A scheduled brief that runs once (W2-24), with `date`. */
    | 'once';
  /** Continuous only: minutes between checks; absent means 30. */
  everyMinutes?: 1 | 2 | 5 | 10 | 15 | 30;
  /** 'YYYY-MM-DD', once only. */
  date?: string;
  /** 2–30, every_n_days only */
  everyNDays?: number;
  /** 0–6 (Mon=0), weekly only */
  weekday?: number;
  /**
   * Monthly only, and exactly one of this or `firstBusinessDay`: 1 to 30, or
   * 31 for the month's last day. 29 and 30 fall back to the last day in a
   * shorter month. Monthly and quarterly are for an agent's play only.
   */
  dayOfMonth?: number;
  /**
   * Monthly only: the first Monday to Friday of the month, in the schedule's
   * time zone. Quarterly always runs on the first business day of January,
   * April, July and October, so it carries neither day field.
   */
  firstBusinessDay?: boolean;
  /** 'HH:MM' (24h) */
  time?: string;
  /** IANA key, captured silently from the browser */
  timezone?: string;
}

/**
 * "Trigger this play from: CRM | Cleanlist" (§5.0; `manualKind` DELETED —
 * there is no manual run; all initiation is the poll or Run Test).
 * Record kind emitted: CRM Account → company; CRM Contact/Lead → person;
 * Cleanlist people → person; Cleanlist company → company.
 */
export interface IncomingWebhookConfig {
  preset?:
    | 'rb2b_visitor'
    | 'segment_track'
    | 'calendly_booked'
    | 'calendly_no_show';
  recordKind: 'person' | 'company';
  identity: Partial<
    Record<
      | 'email'
      | 'linkedinUrl'
      | 'firstName'
      | 'lastName'
      | 'companyName'
      | 'companyDomain',
      string
    >
  >;
  fields: {
    key: string;
    path: string;
    type: 'text' | 'number' | 'boolean' | 'date';
  }[];
  eventIdPath?: string;
  signature?: 'none' | 'hmac_sha256';
}

/** The saved incoming source's endpoint health (G24-8b), without the URL
 *  token, the signing secret or the untrusted sample. Null: no endpoint. */
export interface WebhookEndpointHealth {
  urlHint: string;
  createdAt: string | null;
  lastReceivedAt: string | null;
  pending: number;
  received24h: number;
  rejected24h: number;
  hasSample: boolean;
  /** The saved signature setting against this endpoint's secret: a play
   *  saved as signed whose URL predates it has no secret until Rotate. */
  signing: 'on' | 'off' | 'no_secret' | null;
}

export interface WebhookSamplePreview {
  previewOnly: true;
  sampleUntrusted: true;
  sample: Record<string, unknown>;
  sampleTruncated: boolean;
  paths: { path: string; type: string }[];
  identity: Record<string, unknown>;
  trigger: { kind: 'webhook'; fields: Record<string, unknown> };
  invalidFields: number;
  issues: { code: string; message: string }[];
}

export type CompanySignalKind =
  | 'job_posting'
  | 'news'
  | 'funding'
  | 'headcount';
/** Target accounts read from the CRM at every check: every account
 *  (``crm_accounts``) or the ones a field marks
 *  (``field:account.<Field>:<op>[:<value>]``). */
export interface CrmAccountView {
  crmType: PlayCrm;
  condition: string;
}
export interface CompanySignalConfig {
  accounts: {
    domains: string[];
    fromList?: { listId: string } | null;
    fromCrm?: CrmAccountView | null;
  };
  signals: CompanySignalKind[];
  jobPosting?: { titleKeywords?: string[]; functions?: string[] };
  news?: { keywords?: string[] };
  headcount?: { growth6mMinPercent?: number | null };
  freshness?: 'monthly';
}

/** A call tool: each has its own trigger and its own Get call notes step. */
export type CallTool = 'fireflies' | 'fathom' | 'gong';

/**
 * "A Fireflies call is recorded" (`fireflies`) or "A Fathom call is recorded"
 * (`fathom`): the settings under the trigger's own tool key, all optional.
 */
export interface CallTriggerConfig {
  who?: 'external_participants' | 'first_external_participant';
  maxPerMeeting?: number;
  titleContains?: string[];
  minDurationMinutes?: number;
  backfill?: 'now' | 'historic';
}

export interface MeetingScan {
  meetingsRead: number;
  participants: number;
  skippedInternal: number;
  skippedNoSummary: number;
}

export type SheetFieldType = 'string' | 'number' | 'date' | 'boolean';
export type SheetDateFormat = 'YYYY-MM-DD' | 'M/D/YYYY' | 'D/M/YYYY';
export interface SheetColumn {
  key: string;
  header: string;
  type: SheetFieldType;
  format?: SheetDateFormat | null;
}
export interface SheetFilters {
  spreadsheetId: string;
  spreadsheetName: string;
  spreadsheetUrl?: string | null;
  sheetId: number;
  sheetName: string;
  googleUserId: string;
  accountLabel?: string | null;
  recordKind: 'person' | 'company';
  headerRow: 1;
  columns: SheetColumn[];
  identity: Partial<
    Record<
      | 'email'
      | 'linkedinUrl'
      | 'firstName'
      | 'lastName'
      | 'fullName'
      | 'title'
      | 'companyName'
      | 'companyDomain'
      | 'externalId',
      string | null
    >
  >;
  rowKey: string | null;
  backfill: 'now' | 'historic';
  /** Absent means new rows; `all_rows` reads every row once each period. */
  scope?: 'new_rows' | 'all_rows';
  /** Absent means on; false keeps a row's email out of the CRM push. */
  rowEmailToCrm?: boolean;
  perPeriod?: number | null;
}

/** What a sequence's run does that can start a play. */
export type SequenceTriggerEvent =
  | 'replied'
  | 'finished'
  | 'finished_no_reply'
  | 'bounced'
  | 'out_of_office'
  | 'meeting_booked'
  | 'invite_accepted'
  | 'call_outcome'
  | 'link_clicked'
  | 'left_company'
  | 'step_reached';

export interface TriggerConfig {
  /** The trigger card's name, on every kind (PROD-16). */
  name?: string;
  source:
    | 'crm'
    | 'cleanlist'
    | 'crm_duplicates'
    | 'job_change'
    | 'crustdata_watch'
    | 'company_signal'
    | 'lead_list'
    | 'google_sheet'
    | 'webhook'
    | 'play'
    | 'fireflies'
    | 'fathom'
    | 'gong'
    | 'calendar'
    | 'google_doc'
    | 'sequence'
    | 'notion'
    /** A schedule with no records: an agent's scheduled brief (W2-24). */
    | 'schedule';
  /** Events of a Cleanlist sequence's runs that start the play. */
  sequence?: {
    sequenceId?: string;
    sequenceName?: string;
    events: SequenceTriggerEvent[];
    /** Reply labels the play is limited to; only with `replied`. */
    labels?: string[];
    /** Call outcomes the play is limited to; only with `call_outcome`. */
    outcomes?: string[];
  };
  /** A receiving play does not scan an external source. */
  play?: { recordKind: 'person' | 'company' };
  /**
   * What starts the play, in the user's terms. Additive on top of `source`:
   * the backend still reads `source`/`crm`/`cleanlist`, and a graph saved
   * before this field existed resolves through `triggerKind()`.
   */
  kind?: TriggerKind;
  calendar?: CalendarTriggerConfig;
  /** The Google Docs the member picked or pasted (`google_doc_updated`). */
  googleDoc?: GoogleDocTriggerConfig;
  /**
   * For `crm_duplicates` only `crmType` travels (`{ crmType: 'salesforce' }`,
   * W5 design 2.2); read it with `cleanupCrmType`, never `objectType`.
   */
  crm?: CustomCrmTriggerConfig;
  cleanlist?: CleanlistTriggerConfig;
  fireflies?: CallTriggerConfig;
  fathom?: CallTriggerConfig;
  gong?: CallTriggerConfig;
  /** Tab selected through the picking member’s Google grant. */
  sheet?: Partial<SheetFilters>;
  /** A Notion database, named by its pasted link or id. */
  notion?: Partial<NotionTriggerBlock>;
  watch?: {
    toolFilters: ToolFilters;
    companyFilters: CompanySearchFilters;
    perPeriod?: number;
  };
  companySignal?: CompanySignalConfig;
  /** Incoming identity and typed fields only. Endpoint credentials stay separate. */
  webhook?: IncomingWebhookConfig;
  /** Existing Cleanlist leads, without importing or charging them. */
  leadList?: {
    listId?: string;
    listName?: string;
    backfill?: 'now' | 'historic';
  };
  /** kind = 'job_change' only */
  jobChange?: JobChangeConfig;
  /** absent = continuous (§2a) */
  schedule?: TriggerSchedule;
  /** Agent play only, 1..365. Absent means each record runs once. */
  rerunAfterDays?: number;
  /** Agent play only, 1..365. Absent means no cross-play exclusion. */
  skipTouchedWithinDays?: number;
  /**
   * `crm_duplicates` only (W5 design 2.2): the ruleset version the config was
   * written under. Internal, never shown.
   */
  cleanupVersion?: number;
  /** `crm_duplicates` only: which CRM objects the scan checks. */
  objects?: CleanupObject[];
  /** `crm_duplicates` only: what counts as a duplicate, per object. */
  match?: CleanupMatch;
  /** `crm_duplicates` only: records that never merge. */
  exclude?: CleanupExclude;
}

/* ── CRM cleanup rules (W5 design, section 2) ───────────────────────── */

/**
 * A CRM object a cleanup play checks. Salesforce has account, contact and
 * lead; HubSpot has company and contact.
 */
export type CleanupObject = 'account' | 'company' | 'contact' | 'lead';

/**
 * A match rule, a boolean per object (design 2.3). Accounts and companies take
 * `domain` and `name_no_domain`; contacts take `email`, `linkedin`,
 * `cleanlist_id` and `name_in_account`; leads take the first three and
 * `name_in_company`.
 */
export type CleanupMatchRule =
  | 'domain'
  | 'name_no_domain'
  | 'email'
  | 'linkedin'
  | 'cleanlist_id'
  | 'name_in_account'
  | 'name_in_company';

export type CleanupMatch = Partial<
  Record<CleanupObject, Partial<Record<CleanupMatchRule, boolean>>>
>;

/**
 * A CRM property condition that excludes a record, in the design's wire
 * shape: `{ field: 'Type', op: 'eq', value: 'Customer' }`.
 */
export interface CleanupCondition {
  field: string;
  op: string;
  value?: unknown;
}

export interface CleanupExclude {
  /** Records owned by these CRM users never merge. */
  ownerIds: string[];
  /** Records created in the last N days (0 to 365; 0 = off). */
  createdWithinDays: number;
  conditions: Partial<Record<CleanupObject, CleanupCondition[]>>;
}

/** Survivorship: the first rule that separates the members picks the kept record. */
export type CleanupKeepRule =
  | 'open_deals'
  | 'owner_is_rep'
  | 'oldest'
  | 'newest'
  | 'recent_activity'
  | 'most_complete';

/** `fill_blanks` fills the kept record's blanks; `keep_master` writes no fields. */
export type CleanupFieldMode = 'fill_blanks' | 'keep_master';

/** `review` waits for a person; `auto_exact` merges exact contact and lead groups. */
export type ReviewPolicy = 'review' | 'auto_exact';

/** Review duplicates step (`review_duplicates`, design 5.3). */
export interface ReviewDuplicatesConfig {
  policy: ReviewPolicy;
}

/** Merge duplicates step (`merge_duplicates`, design 5.4). */
export interface MergeDuplicatesConfig {
  keep: Partial<Record<CleanupObject, CleanupKeepRule[]>>;
  fields: Partial<Record<CleanupObject, CleanupFieldMode>>;
}

/**
 * The trigger types the picker offers. The first three watch for a change in
 * the CRM; the last three sweep on a schedule and pick up what is new.
 *
 * Each one narrows to a `source` the backend already understands, so this is
 * vocabulary for the UI rather than a new wire contract:
 *   crm_property_change → source 'crm', conditionType 'property'
 *   crm_list_membership → source 'crm', conditionType 'list_membership'
 *   job_change          → source 'job_change', CRM Contact watch
 *   new_people          → source 'cleanlist', kind 'people'
 *   new_companies       → source 'cleanlist', kind 'company'
 *   crm_duplicates      → source 'crm_duplicates' (W5: an agent's CRM
 *                         cleanup play, which emits duplicate groups)
 */
export interface CalendarTriggerConfig {
  googleUserIds: string[];
  event: 'booked' | 'starting' | 'no_notes';
  hoursBefore?: number | null;
  who: 'external_attendees';
  maxPerMeeting: number;
  titleContains: string[];
}

export type TriggerKind =
  | 'deal_stage'
  | 'deal_stalled'
  | 'sixsense_stage'
  | 'crm_property_change'
  | 'crm_list_membership'
  | 'job_change'
  | 'new_people'
  | 'new_companies'
  | 'new_jobs'
  | 'post_engagers'
  | 'fireflies_call'
  | 'fathom_call'
  | 'gong_call'
  | 'tool_adopted'
  | 'company_signal'
  | 'list_leads_added'
  | 'sheet_rows_added'
  | 'webhook'
  | 'sequence_event'
  | 'play_handoff'
  | 'crm_duplicates'
  | 'meeting_booked'
  | 'meeting_starting'
  | 'meeting_no_notes'
  | 'notion_page_added'
  | 'notion_page_updated'
  | 'google_doc_updated'
  /** An agent's scheduled brief (`source: 'schedule'`, W2-24). */
  | 'schedule'
  | 'one_time';

/** Notion property types a step writes. */
export type NotionWritableType =
  | 'title'
  | 'rich_text'
  | 'number'
  | 'select'
  | 'multi_select'
  | 'date'
  | 'url'
  | 'email'
  | 'phone_number'
  | 'checkbox';

/** Notion property types a trigger reads as record fields. */
export type NotionReadableType =
  | NotionWritableType
  | 'status'
  | 'unique_id'
  | 'created_time'
  | 'last_edited_time'
  | 'formula';

export interface NotionTriggerProperty {
  key: string;
  name: string;
  type: NotionReadableType;
}

/** A Notion trigger's block (`notion_trigger.py`). */
export interface NotionTriggerBlock {
  databaseId: string;
  databaseName?: string;
  databaseUrl?: string | null;
  recordKind: 'person' | 'company';
  properties: NotionTriggerProperty[];
  identity: Partial<
    Record<
      | 'email'
      | 'linkedinUrl'
      | 'firstName'
      | 'lastName'
      | 'fullName'
      | 'title'
      | 'companyName'
      | 'companyDomain',
      string | null
    >
  >;
  backfill: 'now' | 'historic';
}

/** One mapped Notion property: from a record field or a value. */
export interface NotionPropertyMapping {
  name: string;
  type: NotionWritableType;
  sourceField?: string;
  value?: string | number | boolean | string[];
}

/** Add a page / Update a Notion page (`notion_add_page`, `notion_update_page`). */
export interface NotionStepConfig {
  databaseId?: string;
  databaseName?: string;
  databaseUrl?: string | null;
  properties: NotionPropertyMapping[];
  /** Update only. */
  match?: {
    by: 'property' | 'page_id';
    property?: string;
    type?: NotionWritableType;
    sourceField?: string;
    value?: string;
  };
  /** Update only. */
  whenNotFound?: 'skip' | 'add';
}

export function isNotionTriggerKind(kind: TriggerKind | undefined): boolean {
  return kind === 'notion_page_added' || kind === 'notion_page_updated';
}

/** One Salesforce report a job change watches (scope 'reports'). */
export interface JobChangeReport {
  /** The report's 15- or 18-character id (00O…). */
  id: string;
  /** The report's name when it was picked, shown once it's unavailable. */
  name?: string | null;
  /** The tag its Contacts carry as `trigger.relationship` (1 to 255
   *  characters), e.g. "Champion". */
  relationship: string;
}

/**
 * Job change tracking. Watches contacts already in the CRM and fires when the
 * person turns up somewhere new.
 */
export interface JobChangeConfig {
  crmType: 'salesforce' | 'hubspot';
  /** 'all' watches every contact; 'list' narrows to one list or campaign;
   *  'filter' to the Contacts matching `conditions`; 'reports' (Salesforce
   *  only) to the Contacts in `reports`. */
  scope: 'all' | 'list' | 'filter' | 'reports';
  listId?: string | null;
  listName?: string | null;
  /** scope 'filter' only: the Contact conditions, in the shape a CRM record
   *  trigger keeps under `crm.conditions`. */
  conditions?: CrmTriggerConfig['conditions'];
  /** scope 'reports' only: 1 to 10 reports in priority order. Someone in
   *  more than one takes the first one's relationship. */
  reports?: JobChangeReport[];
  /** Missing only on a legacy graph, displayed as monthly until saved. */
  freshness?: 'monthly' | 'biweekly';
  /** What counts as a move. At least one must be on. */
  watch: { company: boolean; title: boolean };
}

/**
 * Resolve a saved trigger to its kind. Graphs written before `kind` existed
 * carry only `source`, so read it back off the config they do have.
 */
export function triggerKind(
  config: Partial<TriggerConfig> | undefined
): TriggerKind | undefined {
  if (!config) return undefined;
  // 'crm_criteria' was a second picker row that built exactly the same config
  // as a property change. It is one trigger now, and the "records to pick up"
  // choice carries the difference it used to encode.
  const saved = config.kind as string | undefined;
  if (saved === 'crm_criteria') return 'crm_property_change';
  if (saved) return saved as TriggerKind;
  if (config.source === 'fireflies') return 'fireflies_call';
  if (config.source === 'fathom') return 'fathom_call';
  if (config.source === 'gong') return 'gong_call';
  if (config.source === 'calendar')
    return `meeting_${config.calendar?.event ?? 'booked'}`;
  if (config.source === 'crustdata_watch') return 'tool_adopted';
  if (config.source === 'company_signal') return 'company_signal';
  if (config.source === 'crm_duplicates') return 'crm_duplicates';
  if (config.source === 'lead_list') return 'list_leads_added';
  if (config.source === 'google_sheet') return 'sheet_rows_added';
  if (config.source === 'google_doc') return 'google_doc_updated';
  if (config.source === 'webhook') return 'webhook';
  if (config.source === 'sequence') return 'sequence_event';
  if (config.source === 'play') return 'play_handoff';
  if (config.source === 'notion') return 'notion_page_added';
  if (config.source === 'schedule')
    return config.schedule?.frequency === 'once' ? 'one_time' : 'schedule';
  if (config.source === 'cleanlist') {
    if (config.cleanlist?.kind === 'jobs') return 'new_jobs';
    if (config.cleanlist?.kind === 'posts') return 'post_engagers';
    return config.cleanlist?.kind === 'company'
      ? 'new_companies'
      : 'new_people';
  }
  if (
    config.source === 'job_change' ||
    (config.source === 'crm' && config.jobChange)
  )
    return 'job_change';
  if (config.source === 'crm') {
    if (config.crm?.conditionType === 'stage_change') return 'deal_stage';
    if (config.crm?.conditionType === 'stalled') return 'deal_stalled';
    if (config.crm?.conditionType === 'sixsense_stage') return 'sixsense_stage';
    return config.crm?.conditionType === 'list_membership'
      ? 'crm_list_membership'
      : 'crm_property_change';
  }
  return undefined;
}

export function isCalendarTriggerKind(kind: TriggerKind | undefined): boolean {
  return (
    kind === 'meeting_booked' ||
    kind === 'meeting_starting' ||
    kind === 'meeting_no_notes'
  );
}

/** The `source` a kind narrows to. */
export function triggerSourceFor(kind: TriggerKind): TriggerConfig['source'] {
  if (kind === 'fireflies_call') return 'fireflies';
  if (kind === 'fathom_call') return 'fathom';
  if (kind === 'gong_call') return 'gong';
  if (isCalendarTriggerKind(kind)) return 'calendar';
  if (kind === 'job_change') return 'job_change';
  if (kind === 'tool_adopted') return 'crustdata_watch';
  if (kind === 'company_signal') return 'company_signal';
  if (kind === 'crm_duplicates') return 'crm_duplicates';
  if (kind === 'list_leads_added') return 'lead_list';
  if (kind === 'sheet_rows_added') return 'google_sheet';
  if (kind === 'google_doc_updated') return 'google_doc';
  if (kind === 'webhook') return 'webhook';
  if (kind === 'sequence_event') return 'sequence';
  if (kind === 'play_handoff') return 'play';
  if (isNotionTriggerKind(kind)) return 'notion';
  if (kind === 'schedule' || kind === 'one_time') return 'schedule';
  return kind === 'new_people' ||
    kind === 'new_companies' ||
    kind === 'new_jobs' ||
    kind === 'post_engagers'
    ? 'cleanlist'
    : 'crm';
}

/**
 * Per-step wait, shown on the top strip of the node card and edited in the
 * inspector. Absent or 0 days = the step runs as soon as the record reaches
 * it, which is what every saved graph does today.
 */
export interface StepDelay {
  days: number;
}

/** Every step except the trigger can be held back by a delay. */
export interface WithDelay {
  delay?: StepDelay;
}

export interface GetCompanyConfig {
  source: 'employer' | 'crm_account';
}

/**
 * Identify person (cleanse-api `utils/play_identify_person.py`, agent plays
 * only): finds each person's LinkedIn profile with the employment check's own
 * lookups and lists every candidate it weighed. It has no options, so a saved
 * step holds only the step delay. Outputs read downstream as
 * `step.<nodeId>.linkedin_url` and the rest of the server's
 * `play_identify_person.OUTPUTS`. It costs the lookups that found something
 * (a reverse email lookup that found a profile, a person identified), so the
 * price is read from the validate costs.
 */
export type IdentifyPersonConfig = WithDelay;

/**
 * Match company (cleanse-api `utils/play_match_company.py`, agent plays only):
 * each person's account against the job history an upstream Job history step
 * stored, with the employment check's own rules. Outputs read downstream as
 * `step.<nodeId>.still_at_account`, `confidence`, `needs_review` and the rest
 * of the server's `play_match_company.OUTPUTS`. Free.
 * - `freshestCopy` (default true): the copy whose roles are newer decides,
 *   toward No only.
 * - `invalidEmailMeansLeft` (default false): an invalid work email on the
 *   account's domain answers No.
 * - `neverAtAccount` (default false): a profile that never lists the account,
 *   current at the work email's company, answers No at Medium.
 * - `accountLinkedinField`: a field holding the account's LinkedIn page.
 */
export interface MatchCompanyConfig extends WithDelay {
  freshestCopy?: boolean;
  invalidEmailMeansLeft?: boolean;
  neverAtAccount?: boolean;
  accountLinkedinField?: string | null;
}

/** Get Fireflies call notes / Get Fathom call notes. */
export interface CallNotesConfig {
  recency?: 'latest' | 'within_days';
  withinDays?: number;
  /** Fireflies and Fathom only: also read the call's transcript. */
  transcript?: boolean;
}

export type CompanyFieldConfig = Record<string, never>;

export interface EnrichConfig extends WithDelay {
  target: 'person' | 'company';
  person?: EnrichPersonConfig;
}

/**
 * What an Enrich step finds for each person. `reverseLookup`,
 * `personalEmail` and `phoneMode` run on an agent's play only (W1-34,
 * W1-35); a graph without them reads as before.
 */
export interface EnrichPersonConfig {
  profile: boolean;
  email: boolean;
  phone: boolean;
  /** Find the LinkedIn profile from the email of people who have no URL. */
  reverseLookup?: boolean;
  /** Find personal email addresses. */
  personalEmail?: boolean;
  /** `mobile`: only an explicit or carrier-checked mobile counts as found. */
  phoneMode?: 'any' | 'mobile';
}

export interface FindPeopleConfig extends WithDelay {
  audienceFilters: SearchFilters;
  audienceCompanyFilters: CompanySearchFilters;
  /**
   * Crustdata wire payload derived FE-side at save by
   * `withDerivedTriggerWire` (graph-ops) from `audienceFilters` — the same
   * `audience_wire_filters` precedent as the Cleanlist trigger wire (build-v3
   * §2b S1). The BE consumes ONLY this (never the raw blob, which is kept for
   * re-editing); absent on older drafts → BE skips loudly.
   */
  audienceWireFilters?: Record<string, unknown>;
  /** 1..25 */
  maxPeoplePerAccount: number;
  /** already-in-CRM exclusion on the fan-out (§2c X9; canonical path per CR-5) */
  skipInCrm?: SkipInCrmConfig;
}

/** The tools the agent-instructions option may use on each record. */
export type AgentSelfTool =
  | 'web_search'
  | 'fetch_url'
  | 'crm_record'
  | 'knowledge'
  | 'linkedin_profile';

/**
 * The agent-instructions skill option (`"self"` in `agentIds`, W2-48): the
 * play's own agent judges each record with its instructions, skills and
 * knowledge, and returns typed outputs later steps read as
 * `agent.self.<key>`. Agent plays only.
 */
export interface AgentSelfConfig {
  task: string;
  outputs: RecordSkillOutputSchema | null;
  tools?: AgentSelfTool[];
  /** 0..6 a record, 3 when absent. */
  maxToolCalls?: number;
}

/** agent ids: 'builtin:research' | 'builtin:qualification' | custom-agent UUID
 *  | 'self' (the agent's own instructions, agent plays only) */
export interface ResearchConfig extends WithDelay {
  agentIds: string[];
  /** Present exactly when `builtin:write` is picked. */
  write?: WriteOptions;
  self?: AgentSelfConfig;
  /** Employment check: a person whose original work email at the account
   *  no longer delivers reads as departed. Off when absent. */
  invalidEmailIsDeparture?: boolean;
}

export interface ValidateConfig extends WithDelay {
  email: boolean;
  phone: boolean;
}

/** Play Look up retains the Contact contract and adds owned Account reads. */
export interface CrmActionLookupConfig extends Omit<
  CrmLookupConfig,
  'identifiers' | 'identifier_mappings' | 'object_type'
> {
  identifiers: (CrmLookupIdentifier | 'domain')[];
  identifier_mappings?: Partial<Record<CrmLookupIdentifier | 'domain', string>>;
  object_type?: 'Contact' | 'Lead' | 'Account';
  fields?: string[];
  related?: {
    deals?: boolean;
    activities?: { days: number };
    notes?: { limit: number };
    contacts?: { limit: number };
    /** The 6sense package's scores for the record's Account, as
     *  crm.sixsense.* (Salesforce only, sixsense_v1). */
    sixsense?: boolean;
  };
}

export interface CrmActionConfig extends WithDelay {
  crmType: PlayCrm;
  action:
    | 'lookup'
    | 'sync'
    | 'update_record'
    | 'set_owner'
    | 'create_task'
    | 'create_note'
    | 'add_to_campaign'
    | 'add_contact_role';
  /** Account is a company cohort; HubSpot displays it as Company. */
  objectType?: 'Contact' | 'Lead' | 'default' | 'Account';
  mappingMode?: 'default' | 'custom';
  mappings?: FieldMappingItem[];
  // 'default' = the org's SAVED CRM field mapping, resolved AT EXECUTION TIME;
  // `mappings` ignored. 'custom' requires non-empty mappings; person
  // cohorts additionally require an email-target mapping.
  /** operation='lookup' only */
  lookupConfig?: CrmActionLookupConfig;
  /** owner assignment on sync (build-v3 §2e); absent = keep */
  assignment?: CrmAssignmentConfig;
  /**
   * Owned-record write posture (build-v3 §2g RB4). 'inherit' (default,
   * org-safe) = the org's `owner_reassign_policy`/update gates as today;
   * 'update_owned' = this playbook may update owned records (inbound
   * route-back template default).
   */
  writeSafety?: 'inherit' | 'update_owned';
  /**
   * Sync only. A sync profile (CLE-560) the step runs under instead of the
   * org's default rules. The profile owns the record type, the mapping and the
   * write posture, so the API requires `objectType` to be the profile's
   * record type, `mappingMode: 'default'` with no custom mappings, and
   * `writeSafety` absent or 'inherit'. Absent = the org's default rules.
   */
  syncProfileId?: string;
  /**
   * Action-specific configs omit the legacy Sync mapping keys. On Sync,
   * absent retains the ordinary create-or-update path.
   */
  target?: 'record' | 'new_record' | 'account' | 'deal';
  fields?: {
    field: string;
    /** A template. Absent on a round-robin row. */
    value?: string;
    /** In place of a value (Salesforce and HubSpot, one row a step): a
     *  round robin across chosen CRM users (`round_robin_selected`) into a
     *  Salesforce User lookup or a HubSpot owner property, filling it only
     *  when empty. */
    assignment?: CrmBaseAssignment;
    writeMode?: 'fill_if_empty' | 'overwrite_if_different' | 'always_overwrite';
  }[];
  once?: 'per_record' | 'per_target';
  task?: {
    subject: string;
    body?: string;
    type?: 'call' | 'email' | 'todo';
    dueInDays?: number;
    dueDate?: string;
    priority?: 'high' | 'normal' | 'low';
  };
  note?: { title: string; body: string };
  /**
   * Task or note on the record: also file a Contact's activity on its
   * company and open deal. Off by default; a step saved without it files on
   * the person only.
   */
  linkCompanyAndDeal?: boolean;
  campaign?: { id: string; name?: string; status?: string };
  role?: string;
  primary?: boolean;
}

/**
 * Round-robin owner assignment (build-v3 §2e). `userIds` are CRM user ids
 * (round-robin targets CRM seats; `crm_user_mapping` stays what it is).
 * 'keep' = today's behavior (acting-user mapping, else CRM default-owner).
 */
export interface CrmBaseAssignment {
  mode: 'keep' | 'specific' | 'round_robin_all' | 'round_robin_selected';
  /** specific ⇒ exactly 1; round_robin_selected ⇒ ≥ 1; else unused */
  userIds?: string[];
}

export interface CrmOwnerRule {
  key: string;
  label?: string;
  conditions: BranchFilterGroup;
  userIds: string[];
}

export interface CrmAssignmentConfig {
  /** record_owner is offered only on native Task creation. */
  mode:
    | CrmBaseAssignment['mode']
    | 'account_owner'
    | 'from_field'
    | 'rules'
    | 'record_owner';
  userIds?: string[];
  field?: string;
  rules?: CrmOwnerRule[];
  fallback?: CrmBaseAssignment;
}

/** ordered; exactly one null (= else) allowed, must be last */
export interface BranchArm {
  key: string;
  label: string;
  conditions: BranchFilterGroup | null;
}

/** 2..6 arms */
export interface BranchConfig extends WithDelay {
  branches: BranchArm[];
}

/** Summary, digest and record delivery share the existing Notify step. */
export interface NotifyConfig extends WithDelay {
  /** Slack and Clu in Teams support channels or linked Clu DMs. */
  channel: 'slack' | 'email' | 'teams';
  /** Slack only — the chosen channel. */
  channelId?: string;
  /** Microsoft Teams channel (webhook) id from the Teams integration. */
  webhookId?: string;
  /** Microsoft Teams channel where the Clu app is installed. */
  teamsChannelId?: string;
  channelName?: string;
  deliver?: 'summary' | 'list' | 'per_record';
  to?: 'default' | 'record_owner' | 'play_owner';
  /** With `to: 'play_owner'`: DM this teammate (a Clerk user id) instead of
   *  the agent owner. Empty until one is picked, which blocks saving. */
  memberId?: string;
  ownerField?: string;
  fallback?: 'default' | 'skip';
  message?: string;
  subject?: string | null;
  row?: string | null;
  sort?: { field: string; order: 'asc' | 'desc' } | null;
  maxMessages?: number;
  /** Slack or Clu in Teams digest of people: an Approve button that adds the listed
   *  people to this sequence (`utils/play_notify_approval.py`). */
  approve?: { sequenceId: string } | null;
}

/**
 * Adds each person who reaches it to one of the org's Cleanlist sequences,
 * through the sequence's own enrollment rules (`add_to_sequence` in
 * cleanse-api's `utils/play_sequence_step.py`).
 */
/** Where Add to sequence enrolls: a Cleanlist sequence (no `provider`), or
 *  an outside sequencer on an agent's play (cleanse-api
 *  `providers/sequencers/`). */
export type SequenceProvider =
  | 'cleanlist'
  | 'lemlist'
  | 'outreach'
  | 'salesloft'
  | 'nooks'
  | 'instantly';

export type SequenceSenderMode = 'sequence_default' | 'record_owner' | 'user';

/** Who sends an outside sequence's enrollments; ids are the provider's users. */
export interface SequenceSender {
  mode: SequenceSenderMode;
  userId?: string;
  fallbackUserId?: string;
}

export interface AddToSequenceConfig extends WithDelay {
  /** Absent means Cleanlist, the step as it always was. */
  provider?: SequenceProvider;
  /** Every provider's id: an engage sequence, a lemlist campaign, an Outreach
   *  sequence, a Salesloft cadence, a Nooks sequence or an Instantly
   *  campaign. */
  sequenceId?: string;
  /** The name when it was picked, for the card before the list loads. */
  sequenceName?: string;
  /** Play values: key to template, rendered per person at
   *  enrollment and read by the sequence's emails as `{{play.<key>}}`.
   *  On Instantly each key is a custom variable the campaign declares, read
   *  as `{{<key>}}`. lemlist, Outreach, Salesloft and Nooks ignore them. */
  variables?: Record<string, string>;
  sender?: SequenceSender;
  onActiveElsewhere?: 'skip' | 'allow';
  /** What the step does to each person in the sequence. Absent means add,
   *  the step as it always was; pause and remove work on Cleanlist
   *  sequences only (`play_sequence_step.ACTIONS`). */
  action?: SequenceAction;
  /** Pause only: a whole number of days, 1 to 90. The hold lifts itself. */
  pauseDays?: number;
  /** A record-owner sender: the record field holding the owner. */
  ownerField?: string;
}

export type SequenceAction = 'add' | 'pause' | 'remove';

/**
 * Appends each person who reaches it to a Google Sheet in the org's connected
 * Google account (`export` in cleanse-api's `utils/play_export_step.py`). The
 * sheet is created from the builder (drive.file only reaches files the app
 * created), so there's no picker of existing sheets.
 */
export interface ExportConfig extends WithDelay {
  /** `lead_list` (an agent's play, with `columns`): each chosen field into
   *  the agent's own list, in the list column its header names. */
  destination: 'google_sheets' | 'lead_list' | 'company_list';
  /** A saved list's Companies tab (an agent's play). */
  listId?: string;
  listName?: string;
  spreadsheetId?: string;
  spreadsheetName?: string;
  spreadsheetUrl?: string;
  /** The tab rows go to. */
  sheetName?: string;
  /** The member whose Drive holds the sheet; rows go through their Google. */
  googleUserId?: string | null;
  /** That member's Google account, for display. */
  accountLabel?: string | null;
  /** An agent's play: the record fields written, in sheet order. Absent:
   *  the default columns. */
  columns?: ExportColumn[];
  /** `upsert` updates the row whose `keyColumn` cell matches the record,
   *  else appends one (an agent's play, with `columns`). */
  mode?: 'append' | 'upsert';
  /** One of the `columns` headers. */
  keyColumn?: string;
}

export interface ExportColumn {
  /** A record field id at the step. */
  field: string;
  header: string;
}

/**
 * Google Calendar's read-only Find meetings step: the next and the last
 * meeting with each record's contact (attendee email) or company (attendee
 * domain) on the chosen members' calendars. Outputs read downstream as
 * `step.<nodeId>.next_meeting_at` and the rest of the server's
 * `play_find_meetings.OUTPUTS`.
 */
export interface FindMeetingsConfig {
  googleUserIds: string[];
  matchBy?: 'contact' | 'company';
  lookbackDays?: number;
  lookaheadDays?: number;
}

/** The vendors Job history reads a person's LinkedIn profile from. */
export type JobHistorySource = 'crustdata' | 'enrichlayer';

/**
 * Job history (cleanse-api `utils/play_job_history.py`, agent plays only):
 * each person's LinkedIn job history from `profileSource`, checked against
 * `verifySource` (`'none'` for no check). Outputs read downstream as
 * `step.<nodeId>.roles`, `current_company` and the rest of the server's
 * `play_job_history.OUTPUTS`. It costs one credit for each profile copy it
 * loads from a vendor, so the price is read from the validate costs.
 * `sourceField` is an optional field holding the LinkedIn URL.
 */
export interface JobHistoryConfig extends WithDelay {
  profileSource: JobHistorySource;
  verifySource: JobHistorySource | 'none';
  sourceField?: string;
}

/** One Google Doc a trigger watches: its link or id, as picked or pasted. */
export interface GoogleDocRef {
  docId: string;
  title?: string;
  url?: string;
}

/**
 * "A picked Google Doc is updated" (cleanse-api `utils/play_google_doc.py`).
 * The Google connection reaches only the docs a member picks, so the trigger
 * watches up to 20 docs, never a folder. `googleUserId` is the member whose
 * Google Docs reads them; left out, the member who saves the play.
 */
export interface GoogleDocTriggerConfig {
  documents: GoogleDocRef[];
  googleUserId?: string;
  accountLabel?: string;
  backfill?: 'now' | 'historic';
}

/**
 * Create a Google Doc: one doc a record from title and markdown body
 * templates, in the member's Drive (in `folderId` when set). Writes
 * `step.<nodeId>.doc_id`, `doc_url` and `doc_title`.
 */
export interface CreateGoogleDocConfig {
  title: string;
  body: string;
  /** A Drive folder link or id, picked or pasted. */
  folderId?: string;
  folderName?: string;
  folderUrl?: string;
  googleUserId?: string;
  accountLabel?: string;
}

/**
 * Send an email: one email a person from a member's own Gmail or Outlook
 * mailbox, its subject and markdown body templates with the record's fields.
 * `mailboxId` names an engage mailbox the editing member owns; `chosenBy` is
 * stamped by the server from the member who picked it and never sent from
 * here. `mailboxEmail` is for display only. Mirrors cleanse-api
 * `utils/play_mailbox_email.py`. Writes `step.<nodeId>.email_status`,
 * `email_subject` and `sent_from`.
 */
export interface SendMailboxEmailConfig {
  provider: 'google_workspace' | 'microsoft_365';
  mailboxId?: string;
  mailboxEmail?: string;
  chosenBy?: string;
  subject: string;
  body: string;
  /** `draft` saves each email in the mailbox's drafts folder, unsent. */
  mode?: 'send' | 'draft';
}

/**
 * Send on LinkedIn: one LinkedIn message or invitation a person from a
 * member's own LinkedIn account, its text a template with the record's
 * fields (the invitation's note, which may be empty). `accountId` names an
 * engage LinkedIn account the editing member owns; `chosenBy` and
 * `accountName` are stamped by the server. Mirrors cleanse-api
 * `utils/play_linkedin_action.py`. Writes `step.<nodeId>.linkedin_status`
 * and `sent_from`.
 */
export interface SendLinkedInActionConfig {
  accountId?: string;
  accountName?: string;
  chosenBy?: string;
  action: 'message' | 'invite';
  text: string;
}

/**
 * Count people (cleanse-api `utils/play_count_people.py`, agent plays only):
 * for each person or company record, the people at its company who match
 * the audience, the same audience Find People takes. The server reads only
 * `audienceWireFilters`, derived at save by `withDerivedTriggerWire`; the raw
 * blobs are kept for re-editing. Writes `step.<nodeId>.count` and `.status`
 * (`counted`, `capped`, `no_company`). Free.
 */
export interface CountPeopleConfig extends WithDelay {
  audienceFilters: SearchFilters;
  audienceCompanyFilters: CompanySearchFilters;
  audienceWireFilters?: Record<string, unknown>;
}

/**
 * The Instantly capacity gate (cleanse-api `utils/play_instantly_capacity.py`,
 * agent plays only). No settings: its campaigns are the Instantly Add to
 * sequence steps below it. People without room wait at the gate in a retry
 * hold with reason `instantly_capacity`. Free.
 */
export type InstantlyCapacityConfig = WithDelay;

/** Outbound Webhook step. Credentials are saved-secret IDs only. */
export interface WebhookConfig {
  url: string;
  mode?: 'per_record' | 'batch';
  batchSize?: number;
  body?: string | null;
  headers?: { name: string; value: string }[];
  auth?:
    | { type: 'none' }
    | { type: 'bearer' | 'basic' | 'hmac'; secretId: string }
    | { type: 'header'; secretId: string; headerName: string };
  outputs?: RecordSkillOutputSchema | null;
  responseMap?: Record<string, string> | null;
  maxCallsPerPeriod?: number;
}

export interface WebhookTestResult {
  status: number;
  ms: number;
  responseExcerpt: string;
}

/** Deterministic JavaScript step, available only on an agent's play. */
export interface CodeConfig {
  language: 'javascript';
  source: string;
  inputs: string[];
  outputs: RecordSkillOutputSchema | null;
}

export interface CodeCompileResult {
  ok: boolean;
  errors: GraphIssue[];
  ms: number;
}

export type WaitConfig =
  | {
      mode: 'duration';
      amount: number;
      unit: 'hours' | 'days' | 'business_days';
    }
  | {
      mode: 'until_date';
      field: string;
      offsetDays?: number;
      time?: string;
      ifMissing?: 'drop' | 'continue';
      ifPast?: 'drop' | 'continue';
    };

/** Same-agent target identity. The optional name is a saved display label. */
export interface HandOffConfig {
  playbookId?: string;
  playName?: string | null;
}

/** Dashboard approval with optional Slack, Teams or email reminders. Saved verbatim. */
export interface ApprovalConfig {
  approvers: {
    mode: 'play_owner' | 'users' | 'record_owner';
    userIds?: string[];
  };
  notify?: { slack?: boolean; email?: boolean; teams?: boolean };
  timeoutDays?: number | null;
  onTimeout?: 'reject' | 'approve';
  show?: string[];
}

/** The read tools a brief's turn may use (cleanse-api `TASK_TOOLS`). */
export type AgentTaskTool =
  | 'crm_read'
  | 'knowledge'
  | 'lists_read'
  | 'usage_read'
  | 'run_records'
  | 'web_search';

/**
 * A scheduled brief's step (`agent_task`): its read tools (default
 * knowledge) and the most its turn spends (1 to 1,000, default the agent's
 * Brief credits per run). What the brief says lives in the agent's
 * Instructions, never here.
 */
export interface AgentTaskConfig {
  tools?: AgentTaskTool[];
  maxCredits?: number;
}

export type CustomNodeConfig =
  | AgentTaskConfig
  | ApprovalConfig
  | HandOffConfig
  | CodeConfig
  | WaitConfig
  | TriggerConfig
  | EnrichConfig
  | CompanyFieldConfig
  | FindPeopleConfig
  | GetCompanyConfig
  | ResearchConfig
  | ValidateConfig
  | CrmActionConfig
  | BranchConfig
  | AddToSequenceConfig
  | ExportConfig
  | CreateGoogleDocConfig
  | SendMailboxEmailConfig
  | SendLinkedInActionConfig
  | NotifyConfig
  | WebhookConfig
  | FindMeetingsConfig
  | JobHistoryConfig
  | ReviewDuplicatesConfig
  | MergeDuplicatesConfig
  | IdentifyPersonConfig
  | MatchCompanyConfig
  | CountPeopleConfig
  | InstantlyCapacityConfig
  | CallNotesConfig
  | NotionStepConfig;

// ---------------------------------------------------------------------------
// Run DTOs (camelCase mirror of the Pydantic responses)
// ---------------------------------------------------------------------------

export type RunStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'completed_with_errors'
  | 'failed'
  | 'cancelled'
  /** A scheduled brief out of credits: it resumes after a top-up. */
  | 'paused_credits';

export type NodeStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed'
  | 'skipped';

export interface CustomRunNodeStats {
  nodeId: string;
  nodeType: CustomNodeType;
  status: NodeStatus;
  skipReason?: string;
  entered: number;
  succeeded: number;
  failed: number;
  filtered: number;
  creditsUsed: number;
  /** A test's `upperBound`: below a Branch it couldn't route, so its
   *  projection is a maximum. `note`: why, or why a step adds nobody. */
  detail?: Record<string, unknown> & { note?: string; upperBound?: boolean };
}

export interface CustomRunError {
  nodeId?: string;
  stage: string;
  code: string;
  message: string;
}

export interface CustomRunFrontierEntry {
  nodeId: string;
  status: string;
  count: number;
}

export interface CustomRunDetail {
  samplePreview?: TryPreview | null;
  /** Paid read-only sample, still isDryRun=true. Null for ordinary Test/Main. */
  sampleSize?: number | null;
  skipped?: number;
  /** W2-56: the picked record a test or sample started from. */
  testRecord?: RunTestRecord | null;
  id: string;
  playbookId: string;
  status: RunStatus;
  /** Run Test dry-run (build-v2-plan §6.2): projects, never spends/mutates. */
  isDryRun: boolean;
  /** Destination list this run wrote to — for the overlay's "View leads" link.
   *  null for a dry run or before a list exists. */
  leadListId?: string | null;
  graphSnapshot: CustomGraph;
  recordsTotal: number;
  recordsCompleted: number;
  recordsFailed: number;
  creditsUsed: number;
  errors: CustomRunError[];
  nodeStats: CustomRunNodeStats[];
  frontier: CustomRunFrontierEntry[];
  createdAt: string;
  finishedAt?: string;
  /** The schedule period this batch belongs to (agents plan W2). */
  period?: RunPeriod | null;
  /** The search this batch read. */
  scan?: RunScan | null;
  meeting?: MeetingScan | null;
  sheet?: RunSheet | null;
  /** A cleanup play's run in groups (W5); null on every other run. */
  cleanup?: RunCleanup | null;
  review?: RunReview | null;
  brief?: string | null;
  conversationId?: string | null;
  /** Per Wait step, this run's records that still wait now and the next
   *  resume (an agent's play only). */
  waiting?: Record<string, { waiting: number; nextResumeAt: string | null }>;
  /** A test whose Branch couldn't route its projection: what it projects
   *  past that Branch is a maximum. Null on every other run. */
  projection?: { upperBound: boolean; note: string } | null;
  /** A run with nothing to do: every record was skipped for one reason. */
  outcome?: RunOutcome | null;
}

// ---------------------------------------------------------------------------
// List / summary / records DTOs (mirror of §12.2 responses)
// ---------------------------------------------------------------------------

/** Row shape returned by list/get/create/save (`CustomPlaybookDto`). */
/** Server-owned semantic save receipt. It never grants proposal or run authority. */
export type SavedPlayReviewDescriptor = {
  eventId: string;
  conversationId: string;
  playId: string;
  version: number;
  /** False on a later layout/name-only save of the same semantic graph. */
  changed: boolean;
};

export interface CustomPlaybookDto {
  id: string;
  name: string;
  description?: string;
  graph: CustomGraph;
  version: number;
  /** `crm_cleanup` is a `crm_duplicates` trigger (W5 design 5.1). */
  triggerType:
    | 'manual'
    | 'crm_poll'
    | 'cleanlist_poll'
    | 'crm_cleanup'
    | 'handoff';
  crmType?: string | null;
  isEnabled: boolean;
  leadListId?: string | null;
  /** next scheduled check (ISO, §2a SC8); null/absent = continuous or disabled */
  nextRunAt?: string | null;
  createdAt: string;
  modifiedAt: string;
  /**
   * Save only, on a cleanup play: how many approved merges went back to
   * review because the save changed the rules (W5 design 2.7).
   */
  reopenedMerges?: number;
  /** What the poll found on its last checks; null until it has checked. */
  triggerHealth?: TriggerHealth | null;
  /**
   * Save only, on an agent's play: the save changed the play and
   * posted a line about it into the user's chat with the agent.
   */
  chatNotified?: boolean;
  savedReview?: SavedPlayReviewDescriptor | null;
  savedReviewUnavailable?: boolean;
  /**
   * Save only, on an agent's play (CLE-478): what the saved play doesn't do
   * yet and up to three replies that ask Clu to propose the fixes.
   */
  gaps?: {
    code: string;
    message: string;
    reason?: string | null;
    quick_reply?: string | null;
  }[];
  quickReplies?: string[];
}

/** A failure the poll recorded on a play's trigger (`trigger_state.health`). */
export interface TriggerHealthError {
  code: string;
  message?: string | null;
  /** Naive UTC ISO. */
  at: string;
}

/**
 * `trigger_state.health`, written by the poll every tick (triggers hardening
 * H4, H5). Times are naive UTC ISO.
 */
export interface TriggerHealth {
  /** Current CRM baseline progress only, with no cursor or installation data. */
  baseline?: { state: 'building' | 'ready'; count: number };
  checkedAt?: string;
  /** The last tick that fired a run with no failure. */
  firedAt?: string;
  /** `fired`, `nothing_new`, a failure's code or a skip's reason. */
  outcome?: string;
  /** Present only while the checks fail. */
  error?: TriggerHealthError;
  /** A partial source result that did not fail the tick. */
  warning?: TriggerHealthError;
  /** Failed checks in a row with the same code. */
  failures?: number;
  /** A run still live 6 hours or more after it started. */
  liveRun?: { runId: string; since: string };
  /** The poll turned the play off after failed checks; turning it on clears it. */
  paused?: TriggerHealthError;
  /** A HubSpot list read starting over from its first page (W1-31). */
  catchingUp?: boolean;
}

/**
 * The schedule period a poll batch belongs to (`RunSummaryDto.period`, agents
 * plan W2): an agent's play delivers its leads per period in batches, one a
 * tick, and one period is one run to the user. Null on other runs.
 */
export interface RunPeriod {
  /** The schedule slot the period fills (naive UTC ISO), or 'unscheduled'. */
  key: string;
  quota: number;
  /** Leads the period has delivered so far, this batch included; people
   *  Find People delivered when `unit` is 'people'. */
  delivered: number;
  /** Batches that brought leads so far. */
  batch: number;
  /** Search pages, profiles and CRM exclusions the period has read so far. */
  pages: number;
  scanned: number;
  excluded: number;
  /** The period's last batch; its Notify reports the whole period. */
  final: boolean;
  /** Why the period ended: quota_met, exhausted, scan_budget, ... */
  end_reason: string | null;
  prior_run_ids?: string[];
  quota_by?: string | null;
  credit_limit?: number | null;
  kind?: string;
  /** How the summary names the period: "this week", "today", "in these 3 days". */
  words?: string;
  /** 'people' when the trigger seeds companies and Find People follows: the
   *  quota and delivered count people. Absent, they count what the trigger
   *  seeds (`kind`). */
  unit?: 'people';
  /** Companies the period has searched for people so far (a people period). */
  companies?: number;
  /** People this batch's Find People could still deliver (a people period);
   *  null on a run the poll fired without a scan, such as the closing run. */
  people_needed?: number | null;
}

/** The search one poll batch read (`RunSummaryDto.scan`); null otherwise. */
export interface RunSheet {
  rowsRead: number;
  fresh: number;
  skippedNoIdentity: number;
  baselined: number;
  unchanged?: boolean;
}

export interface RunScan {
  pages: number;
  scanned: number;
  excluded: number;
  /** Local calendar date used to resolve this run's rolling criteria. */
  resolvedOn?: string;
  targetAccounts?: number;
  /** People the "In Instantly" options dropped, apart from `excluded`. */
  excludedInstantly?: number;
  /** Instantly leads with no LinkedIn URL, which no search row can be
   *  checked against (the push checks them by email). */
  instantlyUnchecked?: number;
  signals?: Partial<Record<CompanySignalKind, number>>;
  watch_status?: string;
  coverage_note?: string;
}

/**
 * A cleanup play's run in duplicate groups (`RunSummaryDto.cleanup`, W5
 * design 5.6), counted from the groups whose scan or merge run it is. A scan
 * run finds the groups and ends at review; a merge run merges the groups a
 * person merged. Null on every other run.
 */
export interface RunCleanup {
  /** `scan`: the run that found the groups. `merge`: a run that merged them. */
  kind: 'scan' | 'merge';
  found: number;
  byObject: Partial<Record<CleanupObject, number>>;
  exact: number;
  possible: number;
  autoApproved: number;
  /** The run's groups still waiting for a person. */
  open: number;
  approved: number;
  merged: number;
  /** Merge runs: groups skipped because a record changed since review. */
  skipped: number;
  failed: number;
  dismissed: number;
  hubs: number;
  unverified: number;
  truncated: boolean;
  leadsAreContacts: number;
  waitingOnMerge: number;
}

/**
 * Run now or Run Test on a cleanup play with no recent scan of its CRM (W5
 * design 3.3): the server stores the run on the play, starts the scan and
 * answers 202 with this. The run starts when the scan finishes; until then
 * the scan status carries it as `pendingRun`.
 */
export interface QueuedRunDto {
  queued: true;
  scan?: Record<string, unknown> | null;
}

/** A Run now or Run Test answer that waits for its scan, not a started run. */
export function isQueuedRun(value: unknown): value is QueuedRunDto {
  return (
    Boolean(value) &&
    typeof value === 'object' &&
    (value as { queued?: unknown }).queued === true
  );
}

/** Run list row (`RunSummaryDto`). */
/** What an event trigger's test can start from (W2-56). */
export type TestRecordKind = 'contact' | 'deal' | 'enrollment' | 'account';

/** One CRM Contact, Deal, sequence enrollment or watched account a test can start from. */
export interface TestRecordOption {
  id: string;
  label: string;
  detail?: string | null;
}

/** The record a test or sample started from, on its run. */
export interface RunTestRecord {
  kind: TestRecordKind;
  label: string;
}

export interface CustomRunSummary {
  sampleSize?: number | null;
  skipped?: number;
  /** W2-56: the picked record a test or sample started from. */
  testRecord?: RunTestRecord | null;
  /** "resume" for a run that continued parked records. */
  kind?: 'trigger' | 'resume';
  /** Records this run parked, by hold kind. */
  holds?: { retry: number; wait: number; approval: number; handoff: number };
  /** The plays that handed records to this run (Hand off targets). */
  fromPlays?: { id: string; name: string }[];
  id: string;
  playbookId: string;
  status: RunStatus;
  /** dry runs render with a "Test" chip in Run History (§6.2) */
  isDryRun: boolean;
  recordsTotal: number;
  recordsCompleted: number;
  recordsFailed: number;
  creditsUsed: number;
  createdAt: string;
  finishedAt?: string;
  period?: RunPeriod | null;
  scan?: RunScan | null;
  meeting?: MeetingScan | null;
  sheet?: RunSheet | null;
  /** A cleanup play's run in groups (W5); null on every other run. */
  cleanup?: RunCleanup | null;
  /** An agent's play's run review, once it finished (W2-55). */
  review?: RunReview | null;
  /** A scheduled brief's first 280 characters (W2-26). */
  brief?: string | null;
  /** The chat a scheduled brief's run wrote (W2-26). */
  conversationId?: string | null;
  /** Records this run parked that still wait, by hold kind (an agent's
   *  play only; absent when none wait). ``holds`` counts every record it
   *  parked, resumed or not. Retry holds waiting for room in Instantly
   *  count under `capacity`, never under `retry`. */
  waitingHolds?: Partial<Record<PlayHoldCount, number>>;
  /** Why a failed or partly failed run failed: its first error, and the
   *  step's name when the error names one. */
  error?: RunError | null;
  /** A run with nothing to do: every record was skipped for one reason. */
  outcome?: RunOutcome | null;
}

export interface RunError {
  code: string;
  message: string;
  step?: string;
}

/** A run that finished with every record skipped, and why: Find People's
 *  identity guard found no one who still works at the account. */
export interface RunOutcome {
  status: 'skipped';
  code: string;
  reason: string;
}

/**
 * The review every run of an agent's play ends with (W2-55): records in,
 * per Branch arm, sent, written, failures with reasons, credits split into
 * Data (the steps) and Clu (the brief's turn), and the line for the run
 * history row. A scheduled brief's line is its brief's first line.
 */
/**
 * What a failed live run needs (CLE-478): the step, the cause and the fix, in
 * words, and the kind of fix. Absent for a run that needs none. `resolvedAt`
 * is stamped once the play ran again without a failure that matters;
 * `chatId` is the chat holding the message that tells of it.
 */
export type RunFixKind =
  | 'reconnect'
  | 'edit_step'
  | 'raise_limit'
  | 'top_up'
  | 'ask_clu';

export interface RunDiagnosis {
  step: string | null;
  stepId: string | null;
  code: string;
  cause: string;
  fix: string;
  fixKind: RunFixKind;
  known: boolean;
  partial: boolean;
  line: string;
  /** Where a reconnect or top-up is made, for a fix that is a link. */
  link?: { url: string; label: string } | null;
  chatId?: string;
  /** Whose chat `chatId` is: the agent's owner, the only one who can open it. */
  chatUserId?: string;
  messageId?: string;
  resolvedAt?: string;
}

export interface RunReview {
  records: number;
  arms: { nodeId: string; arm: string; records: number }[];
  sent: number;
  written: number;
  failed: number;
  failures: { reason: string; code?: string; records: number }[];
  credits: { data: number; clu: number };
  line: string;
  brief?: string;
  conversationId?: string | null;
  /** A failed live run's diagnosis (CLE-478). */
  diagnosis?: RunDiagnosis | null;
}

export interface RecordDelivery {
  nodeId: string;
  effect: string;
  status: string;
  reason?: string | null;
  to: 'channel' | 'owner' | 'recipients' | 'play_owner';
  at?: string | null;
}

/** One frontier record row in the drill-down drawer (`RunRecordDto`). */
export interface CustomRunRecord {
  fields?: Record<string, unknown>;
  approvalReason?: Record<string, string> | null;
  /** Server-derived original path and verified outputs, requested explicitly. */
  path?: RunRecordPathEntry[];
  outputs?: Record<string, unknown>;
  deliveries?: RecordDelivery[];
  id: string;
  entityType: RecordKind;
  leadId?: string | null;
  companyId?: string | null;
  entity: Record<string, unknown>;
  currentNodeId: string;
  /** node that emitted this row into the frontier (trigger node for seeds) */
  sourceNodeId: string;
  parentRecordId?: string | null;
  /** The record's children in the run by kind and status (a company's people). */
  children?: { entityType: string; status: string; count: number }[];
  status: string;
  error?: Record<string, unknown> | null;
  updatedAt?: string;
  /** Per CRM write step, the CRM record that step wrote (agent plays). */
  crmLinks?: Record<string, { crm: string; url: string }>;
}

/** Paged records response (`RecordsPageDto`) — D3: `{records, nextCursor}`,
 *  no count query; the drawer shows the accumulated `records.length`. */
export interface CustomRunRecordsPage {
  records: CustomRunRecord[];
  nextCursor?: string | null;
}

/**
 * W1: how many CRM records a property trigger matches now, from
 * POST /custom-playbooks/{id}/trigger-matches. An upper bound on what the play
 * runs. `count` is null with `unavailableReason` set when the server could not
 * count (a change trigger, a refused condition, no access, a timeout, a CRM
 * error); that is never a zero.
 */
export interface TriggerMatches {
  count: number | null;
  object: string | null;
  unavailableReason: string | null;
}

export interface TryFailure {
  runId: string;
  signature: string;
  operations: { step: string; code: string }[];
}

export interface TryPreview {
  notApprovable: boolean;
  complete: boolean;
  previewRevision: string | null;
  maxContinuationCredits: number;
  continuationId: string | null;
  reasons: { nodeId: string | null; recordId: string | null; code: string }[];
  plans: Record<string, Record<string, unknown>>;
  /** Set when the Try ran fewer than five records to stay in its budget. */
  budgetStop?: { records: number; limitCredits: number } | null;
}

export interface TryApprovalRequest {
  tryId: string;
  previewRevision: string;
  confirmCredits: number;
}

export interface TryChange {
  kind: 'step' | 'settings' | 'connection';
  change: 'changed' | 'removed' | 'rewired';
  label: string;
}

export interface BuildBudget {
  buildId: string | null;
  state: 'open' | 'needs_user' | 'closed';
  limitCredits: number;
  spentCredits: number;
  reservedCredits: number;
  remainingCredits: number;
  needsUser: boolean;
  changeRequired: boolean;
  lastFailure: TryFailure | null;
  changes: TryChange[] | null;
}

export interface SampleEstimate {
  records: number;
  brief: boolean;
  quoteId: string;
  playVersion: number;
  build: BuildBudget;
  fullRun: {
    recordKind?: 'person' | 'company' | 'brief';
    recordsPerRun: number | null;
    perRunMaxCredits: number | null;
    period: 'week' | 'day' | 'month' | 'quarter';
    perPeriodMaxCredits: number | null;
    budgetLimited: boolean;
  } | null;
  maxCredits: number;
  /** The maximum of 1, 2, ... starting records; a Try runs the most that fit its budget. */
  maxCreditsByRecords: number[];
  balance: number;
  byStep: { nodeId: string; label: string; maxCredits: number }[];
}

export interface RunRecordPathEntry {
  nodeId: string;
  /** The run this step happened in; a resumed record's path spans runs. */
  runId?: string;
  label: string;
  outcome: string;
  outputs?: Record<string, unknown>;
  detail?: Record<string, unknown>;
  arm?: string;
  armLabel?: string;
  /** Set on the step where a hold carried the record into a later run. */
  resumed?: RunRecordResume;
}

/** "Resumed from <run> after <kind>", from the hold that carried the record. */
export interface RunRecordResume {
  fromRunId: string;
  fromRunAt: string | null;
  kind: PlayHoldKind;
  nodeId: string;
  /** The Wait step's saved timing, when a Wait carried it. */
  wait?: Partial<WaitConfig>;
  /** What a retry hold waited for, when the API names it: room in
   *  Instantly for a capacity hold, as hold rows carry it. */
  waitingFor?: 'crm_reconnect' | 'instantly_capacity';
}

/** One node-anchored validator issue (§6.1 errors-vs-warnings split). */
export interface GraphIssue {
  nodeId?: string;
  code: string;
  message: string;
  /** The config key or tool an issue is about (`agent_play_only`, `research_self_tool_off`). */
  key?: string | null;
  line?: number;
  column?: number;
  /**
   * An agent's settings on a step (`agent_ability_off`, `agent_connection_off`,
   * `agent_policy_never`, `agent_acting_user_gone`, and the warning
   * `agent_step_asks`): the app it names, where the user changes it in words
   * (the page builds its own one click from `code`, `key` and `connection`),
   * and who a step that asks first asks.
   */
  connection?: string | null;
  fix?: string | null;
  approver?: string | null;
  /**
   * `crm_sync_unmapped_fields` only: what the Sync step's default field
   * mapping leaves out (`phone`, `linkedin_url`).
   */
  fields?: string[] | null;
}

export interface PlayFieldDescriptor {
  id: string;
  label: string;
  type: 'text' | 'number' | 'boolean' | 'enum' | 'list' | 'date';
  group: string;
  options?: string[];
}

/** `POST /{id}/validate` response (`ValidateGraphResponse`). */
export interface ValidateGraphResponse {
  fields?: Record<string, PlayFieldDescriptor[]>;
  operators?: Record<string, string[]>;
  /** Extra operators one field accepts, by field id (a Branch list). */
  field_operators?: Record<string, string[]>;
  owner_reach?: Record<
    string,
    { slack: number; email: number; teams?: number; of: number } | null
  >;
  ok: boolean;
  /** structural — reject the PUT */
  errors: GraphIssue[];
  /** accepted and persisted — incomplete configs, unwired arms (§6.1) */
  warnings: GraphIssue[];
  /** An agent's play: each step's most a record costs, by node id (W1-10). */
  costs?: Record<string, NodeCost>;
}

/**
 * What a step that reads LinkedIn profile copies adds to its price
 * (`costs[id].profileReads`): `perRead` credits for each copy loaded from a
 * vendor, up to `maxPerRecord` copies, so at most `maxCredits` a record. A
 * copy already loaded, a vendor miss and an outage cost nothing.
 */
export interface ProfileReadsCost {
  perRead: number;
  maxPerRecord: number;
  maxCredits: number;
}

/**
 * A step's price in the validate response: `perRecord` is the most a record
 * costs there. A step that can read LinkedIn profiles also carries its price
 * before the reads (`basePerRecord`) and their bound (`profileReads`), so
 * `perRecord` is `basePerRecord` plus `profileReads.maxCredits`.
 */
export interface NodeCost {
  perRecord: number;
  words: string | null;
  basePerRecord?: number;
  profileReads?: ProfileReadsCost;
  /** A step that charges for each lookup it makes (Identify person) lists
   * them, so `perRecord` is every lookup once. */
  lookups?: NodeCostLookup[];
  /** An Agents step running the agent itself: that option's own price. */
  self?: SelfCost;
}

/**
 * One lookup a step charges for (`costs[id].lookups`): `credits` each time
 * one answers, at most `maxPerRecord` a record, and `when` in words. A miss,
 * an outage and a rate limit cost nothing.
 */
export interface NodeCostLookup {
  kind: string;
  credits: number;
  maxPerRecord: number;
  when: string;
}

/**
 * The agent-instructions option's price a record, as the API quotes it for
 * the org: the model work on the step's task and outputs plus each web tool
 * call it may make, at the org's agent price. The step holds `perRecord`, the
 * most a record costs, and is charged only for the web calls it makes, each
 * at its own tool's price. LinkedIn profile reads are apart
 * (`NodeCost.profileReads`).
 */
export interface SelfCost {
  perRecord: number;
  modelCredits: number;
  /** A call of each web tool the step may call, by tool. */
  callCredits: Partial<
    Record<Extract<AgentSelfTool, 'web_search' | 'fetch_url'>, number>
  >;
  /** The web calls a record can be charged for (0 without a web tool). */
  maxWebCalls: number;
}

// ---------------------------------------------------------------------------
// Request bodies
// ---------------------------------------------------------------------------

export interface SavePlaybookRequest {
  /** Retry persistence of a missing check receipt, through a version-checked save. */
  reviewRequested?: boolean;
  /** Version of the graph being edited. A newer saved version refuses the write. */
  expectedVersion?: number;
  holds?: HoldChoice;
  name: string;
  description?: string;
  /** CustomGraph wire shape; trigger config lives INSIDE the graph's trigger node */
  graph: CustomGraph;
  isEnabled: boolean;
  /** Turn an agent's play on without a Try (409 `try_approval_required` otherwise). */
  skipTry?: true;
  /**
   * Destination list for leads this playbook finds/enriches. A concrete id
   * re-points the playbook's managed list; null/omitted leaves the current
   * binding UNCHANGED (so a client that hasn't learned of an auto-created list
   * can't clobber it). Unset → the first live run auto-creates a visible list.
   */
  leadListId?: string | null;
}

export type PlayHoldKind = 'retry' | 'wait' | 'approval' | 'handoff';
/** A waiting count's key: a hold kind, or `capacity` for the retry holds
 *  the Instantly capacity gate parked (the API's `hold_reads`). The API
 *  sends `capacity` only when some wait. */
export type PlayHoldCount = PlayHoldKind | 'capacity';
export interface PlayHoldsSummary {
  waiting: Record<PlayHoldKind, number> & { capacity?: number };
  nextResumeAt: string | null;
  oldestAt: string | null;
}
export interface PlayHoldRow {
  holdId: string;
  nodeId: string;
  record: {
    kind: 'person' | 'company';
    name: string | null;
    company: string | null;
    domain: string | null;
  };
  resumeAt: string | null;
  since: string;
  /** What a retry hold waits for, when it isn't a plain retry: a CRM
   *  reconnect, or room in Instantly (the capacity gate). */
  waitingFor?: 'crm_reconnect' | 'instantly_capacity';
}
export interface PlayHoldsPage {
  rows: PlayHoldRow[];
  nextCursor: string | null;
}
